import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { useNavigate } from 'react-router-dom';

import {
  getAdminMe,
  listCertificateApplications,
  getCertificateApplication,
  approveCertificateApplication,
  rejectCertificateApplication,
  markCertificateSubmitted
} from '../api';

import AdminShell from './AdminShell';

import '../styles/AdminDashboard.css';
import '../styles/GlassAdmin.css';

const PAGE_SIZE = 8;

const TYPE_LABELS = {
  gst: 'GST',
  msme: 'MSME',
  labour: 'Labour'
};

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected'
};

const PROCESSING_DAYS = {
  gst: [3, 5],
  msme: [1, 2],
  labour: [5, 7]
};

function formatDate(value) {
  if (!value) return '—';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function getStatusClass(status) {
  if (status === 'approved') return 'active';
  if (status === 'rejected') return 'expired';
  return 'pending';
}

function getApplicantName(application) {
  return (
    application.applicant_name ||
    application.primary_contact_name ||
    '—'
  );
}

function getBusinessName(application) {
  return (
    application.trade_name ||
    application.enterprise_name ||
    application.business_name ||
    '—'
  );
}

function addWorkingDays(dateValue, days) {
  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  let added = 0;

  while (added < days) {
    date.setDate(date.getDate() + 1);

    const day = date.getDay();

    if (day !== 0 && day !== 6) {
      added++;
    }
  }

  return date;
}

function formatDeadline(application) {
  if (
    application.status !== 'approved' ||
    application.certificate_status === 'submitted' ||
    !application.reviewed_at
  ) {
    return '—';
  }

  const range =
    PROCESSING_DAYS[application.application_type];

  if (!range) {
    return '—';
  }

  const start = addWorkingDays(
    application.reviewed_at,
    range[0]
  );

  const end = addWorkingDays(
    application.reviewed_at,
    range[1]
  );

  if (!start || !end) {
    return '—';
  }

  return `${formatDate(start)} – ${formatDate(end)}`;
}

export default function VendorCertificateApplications() {
  const navigate = useNavigate();

  const [admin, setAdmin] = useState(null);

  const [applications, setApplications] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /*
   * Queue:
   * all
   * pending
   * approved
   * rejected
   * completed
   */
  const [queueFilter, setQueueFilter] =
    useState('all');

  const [statusFilter, setStatusFilter] =
    useState('all');

  const [searchTerm, setSearchTerm] =
    useState('');

  const [currentPage, setCurrentPage] =
    useState(1);

  const [selectedApplication, setSelectedApplication] =
    useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [actionLoading, setActionLoading] =
    useState(false);

  const [certificateLoading, setCertificateLoading] =
    useState(false);

  const [rejectTarget, setRejectTarget] =
    useState(null);

  const [rejectRemarks, setRejectRemarks] =
    useState('');

  /*
   * Load applications
   */
  const loadApplications = useCallback(
    async ({ silent = false } = {}) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const { data } =
          await listCertificateApplications({
            status:
              statusFilter === 'all'
                ? undefined
                : statusFilter,

            search:
              searchTerm.trim() || undefined
          });

        setApplications(
          Array.isArray(data?.applications)
            ? data.applications
            : []
        );
      } catch (error) {
        console.error(
          'Failed to load certificate applications:',
          error
        );

        setApplications([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [
      statusFilter,
      searchTerm
    ]
  );

  /*
   * Admin authentication
   */
  useEffect(() => {
    getAdminMe()
      .then(({ data }) => {
        setAdmin(data.admin);
      })
      .catch(() => {
        localStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminToken');

        navigate('/login');
      });
  }, [navigate]);

  /*
   * Load / search debounce
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      loadApplications();
    }, 250);

    return () => clearTimeout(timer);
  }, [loadApplications]);

  /*
   * Reset pagination when filters change
   */
  useEffect(() => {
    setCurrentPage(1);
  }, [
    queueFilter,
    statusFilter,
    searchTerm
  ]);

  /*
   * Queue filtering
   */
  const filteredApplications = useMemo(() => {
    return applications.filter(
      (application) => {

        /*
         * PENDING
         *
         * Pending means something still needs
         * to be completed by the admin/process.
         *
         * Includes:
         * submitted
         * under_review
         * approved + certificate not submitted
         */
        if (queueFilter === 'pending') {
          return (
            application.status === 'submitted' ||
            application.status === 'under_review' ||
            (
              application.status === 'approved' &&
              application.certificate_status !== 'submitted'
            )
          );
        }

        /*
         * APPROVED
         *
         * Application itself is approved.
         */
        if (queueFilter === 'approved') {
          return (
            application.status === 'approved' &&
            application.certificate_status !== 'submitted'
          );
        }

        /*
         * REJECTED
         */
        if (queueFilter === 'rejected') {
          return application.status === 'rejected';
        }

        /*
         * COMPLETED
         *
         * Approved application +
         * certificate submitted.
         */
        if (queueFilter === 'completed') {
          return (
            application.status === 'approved' &&
            application.certificate_status === 'submitted'
          );
        }

        /*
         * ALL
         */
        return true;
      }
    );
  }, [
    applications,
    queueFilter
  ]);

  /*
   * Ordering
   */
  const orderedApplications = useMemo(() => {
    const getPriority = (application) => {

      if (
        application.status === 'submitted' ||
        application.status === 'under_review'
      ) {
        return 1;
      }

      if (
        application.status === 'approved' &&
        application.certificate_status !== 'submitted'
      ) {
        return 2;
      }

      if (
        application.status === 'rejected'
      ) {
        return 3;
      }

      if (
        application.status === 'approved' &&
        application.certificate_status === 'submitted'
      ) {
        return 4;
      }

      return 5;
    };

    return [...filteredApplications].sort(
      (a, b) => {
        const priorityA =
          getPriority(a);

        const priorityB =
          getPriority(b);

        if (priorityA !== priorityB) {
          return priorityA - priorityB;
        }

        const dateA = new Date(
          a.submitted_at ||
          a.reviewed_at ||
          a.updated_at ||
          0
        ).getTime();

        const dateB = new Date(
          b.submitted_at ||
          b.reviewed_at ||
          b.updated_at ||
          0
        ).getTime();

        return dateB - dateA;
      }
    );
  }, [
    filteredApplications
  ]);

  /*
   * Pagination
   */
  const totalPages = Math.max(
    1,
    Math.ceil(
      orderedApplications.length /
      PAGE_SIZE
    )
  );

  const visibleApplications = useMemo(() => {
    const start =
      (currentPage - 1) *
      PAGE_SIZE;

    return orderedApplications.slice(
      start,
      start + PAGE_SIZE
    );
  }, [
    orderedApplications,
    currentPage
  ]);

  /*
   * Counts
   */
  const pendingCount =
    applications.filter(
      (item) =>
        item.status === 'submitted' ||
        item.status === 'under_review' ||
        (
          item.status === 'approved' &&
          item.certificate_status !== 'submitted'
        )
    ).length;

  const approvedCount =
    applications.filter(
      (item) =>
        item.status === 'approved' &&
        item.certificate_status !== 'submitted'
    ).length;

  const rejectedCount =
    applications.filter(
      (item) =>
        item.status === 'rejected'
    ).length;

  const completedCount =
    applications.filter(
      (item) =>
        item.status === 'approved' &&
        item.certificate_status === 'submitted'
    ).length;

  /*
   * Open application
   */
  const openApplication = async (
    application
  ) => {
    setDetailLoading(true);

    try {
      const { data } =
        await getCertificateApplication(
          application.application_type,
          application.id
        );

      setSelectedApplication(
        data.application
      );
    } catch (error) {
      alert(
        error.response?.data?.message ||
        'Failed to load application.'
      );
    } finally {
      setDetailLoading(false);
    }
  };

  /*
   * Approve
   */
  const handleApprove = async () => {
    if (!selectedApplication) {
      return;
    }

    setActionLoading(true);

    try {
      await approveCertificateApplication(
        selectedApplication.application_type,
        selectedApplication.id,
        ''
      );

      setSelectedApplication(null);

      await loadApplications({
        silent: true
      });
    } catch (error) {
      alert(
        error.response?.data?.message ||
        'Failed to approve application.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  /*
   * Open rejection modal
   */
  const openReject = () => {
    setRejectTarget(
      selectedApplication
    );

    setRejectRemarks('');
  };

  /*
   * Reject
   */
  const handleReject = async (
    event
  ) => {
    event.preventDefault();

    const remarks =
      rejectRemarks.trim();

    if (!remarks) {
      return;
    }

    setActionLoading(true);

    try {
      await rejectCertificateApplication(
        rejectTarget.application_type,
        rejectTarget.id,
        remarks
      );

      setRejectTarget(null);
      setSelectedApplication(null);

      await loadApplications({
        silent: true
      });
    } catch (error) {
      alert(
        error.response?.data?.message ||
        'Failed to reject application.'
      );
    } finally {
      setActionLoading(false);
    }
  };

  /*
   * Mark certificate submitted
   */
  const handleCertificateSubmitted =
    async () => {

      if (!selectedApplication) {
        return;
      }

      if (
        selectedApplication.status !==
        'approved'
      ) {
        return;
      }

      if (
        selectedApplication.certificate_status ===
        'submitted'
      ) {
        return;
      }

      setCertificateLoading(true);

      try {
        await markCertificateSubmitted(
          selectedApplication.application_type,
          selectedApplication.id
        );

        /*
         * Reload the detail so the modal immediately
         * shows certificate_status = submitted.
         */
        const { data } =
          await getCertificateApplication(
            selectedApplication.application_type,
            selectedApplication.id
          );

        setSelectedApplication(
          data.application
        );

        /*
         * Refresh table so the application moves
         * from Pending to Completed.
         */
        await loadApplications({
          silent: true
        });

      } catch (error) {
        alert(
          error.response?.data?.message ||
          'Failed to mark certificate as submitted.'
        );
      } finally {
        setCertificateLoading(false);
      }
    };

  return (
    <AdminShell
      admin={admin}
      badges={{
        pendingCertificateApplications:
          pendingCount
      }}
      pageTitle="Vendor Certificate Applications"
      searchValue={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search vendors or application IDs..."
    >

      <div className="glass-page">

        {/* PAGE HEADER */}

        <div className="dash-welcome-row">

          <div>

            <h2 className="dash-welcome-title">
              Vendor Certificate Applications

              {pendingCount > 0 && (
                <span className="glass-pill-badge urgent">
                  {pendingCount} Pending
                </span>
              )}
            </h2>

            <p className="dash-welcome-subtitle">
              Review vendor applications for GST,
              MSME, and Labour certificates.
            </p>

          </div>

          <button
            type="button"
            className="dash-modal-btn secondary"
            onClick={() =>
              loadApplications({
                silent: true
              })
            }
            disabled={refreshing}
          >
            <span className="material-symbols-outlined">
              refresh
            </span>

            {refreshing
              ? 'Refreshing...'
              : 'Refresh'}
          </button>

        </div>


        {/* QUEUE FILTER */}

        <div className="glass-panel">

          <div className="glass-tabs">

            {/* ALL */}

            <button
              type="button"
              className={`glass-tab ${
                queueFilter === 'all'
                  ? 'active'
                  : ''
              }`}
              onClick={() =>
                setQueueFilter('all')
              }
            >
              All
            </button>


            {/* PENDING */}

            <button
              type="button"
              className={`glass-tab ${
                queueFilter === 'pending'
                  ? 'active'
                  : ''
              }`}
              onClick={() =>
                setQueueFilter('pending')
              }
            >
              Pending

              {pendingCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    opacity: 0.65
                  }}
                >
                  {pendingCount}
                </span>
              )}
            </button>


            {/* APPROVED */}

            <button
              type="button"
              className={`glass-tab ${
                queueFilter === 'approved'
                  ? 'active'
                  : ''
              }`}
              onClick={() =>
                setQueueFilter('approved')
              }
            >
              Approved

              {approvedCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    opacity: 0.65
                  }}
                >
                  {approvedCount}
                </span>
              )}
            </button>


            {/* REJECTED */}

            <button
              type="button"
              className={`glass-tab ${
                queueFilter === 'rejected'
                  ? 'active'
                  : ''
              }`}
              onClick={() =>
                setQueueFilter('rejected')
              }
            >
              Rejected

              {rejectedCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    opacity: 0.65
                  }}
                >
                  {rejectedCount}
                </span>
              )}
            </button>


            {/* COMPLETED */}

            <button
              type="button"
              className={`glass-tab ${
                queueFilter === 'completed'
                  ? 'active'
                  : ''
              }`}
              onClick={() =>
                setQueueFilter('completed')
              }
            >
              Completed

              {completedCount > 0 && (
                <span
                  style={{
                    marginLeft: 6,
                    opacity: 0.65
                  }}
                >
                  {completedCount}
                </span>
              )}
            </button>

          </div>


          {/* STATUS FILTER */}

          <div
            className="dash-filter-row"
            style={{
              marginTop: 18
            }}
          >

            <select
              className="admin-select"
              value={statusFilter}
              onChange={(event) =>
                setStatusFilter(
                  event.target.value
                )
              }
            >
              <option value="all">
                All Statuses
              </option>

              <option value="submitted">
                Submitted
              </option>

              <option value="under_review">
                Under Review
              </option>

              <option value="approved">
                Approved
              </option>

              <option value="rejected">
                Rejected
              </option>

              <option value="draft">
                Draft
              </option>

            </select>

          </div>

        </div>


        {/* TABLE */}

        <div
          className="glass-directory-panel glass-table-panel"
          style={{
            marginTop: 20
          }}
        >

          <div className="dash-directory-table-wrap">

            {loading ? (
              <div className="dash-empty-state">
                Loading applications...
              </div>

            ) : visibleApplications.length === 0 ? (

              <div className="dash-empty-state">
                No certificate applications found.
              </div>

            ) : (

              <table className="dash-directory-table">

                <thead>

                  <tr>
                    <th>Vendor</th>
                    <th>Email</th>
                    <th>Certificate</th>
                    <th>Applicant</th>
                    <th>Submitted</th>
                    <th>Deadline</th>
                    <th>Status</th>
                    <th>Certificate</th>
                    <th className="right">
                      Action
                    </th>
                  </tr>

                </thead>

                <tbody>

                  {visibleApplications.map(
                    (application) => (

                      <tr
                        key={`${application.application_type}-${application.id}`}
                      >

                        {/* VENDOR */}

                        <td>

                          <div className="glass-vendor-cell">

                            <div className="glass-avatar-tile">

                              {(
                                application.vendor_name ||
                                'V'
                              )
                                .slice(0, 2)
                                .toUpperCase()}

                            </div>

                            <div>

                              <strong>
                                {
                                  application.vendor_name
                                }
                              </strong>

                              <div className="dash-exec-email">
                                ID #
                                {
                                  application.vendor_id
                                }
                              </div>

                            </div>

                          </div>

                        </td>


                        {/* EMAIL */}

                        <td>

                          <span className="dash-exec-email">
                            {
                              application.vendor_email ||
                              application.email_address ||
                              '—'
                            }
                          </span>

                        </td>


                        {/* CERTIFICATE */}

                        <td>

                          <strong>
                            {
                              TYPE_LABELS[
                                application.application_type
                              ]
                            }
                          </strong>

                          <div className="dash-exec-email">
                            {
                              getBusinessName(
                                application
                              )
                            }
                          </div>

                        </td>


                        {/* APPLICANT */}

                        <td>
                          {
                            getApplicantName(
                              application
                            )
                          }
                        </td>


                        {/* SUBMITTED */}

                        <td>
                          {formatDate(
                            application.submitted_at
                          )}
                        </td>


                        {/* DEADLINE */}

                        <td>
                          {formatDeadline(
                            application
                          )}
                        </td>


                        {/* APPLICATION STATUS */}

                        <td>

                          <span
                            className={`glass-status-badge ${getStatusClass(
                              application.status
                            )}`}
                          >
                            {
                              STATUS_LABELS[
                                application.status
                              ] ||
                              application.status
                            }
                          </span>

                        </td>


                        {/* CERTIFICATE STATUS */}

                        <td>

                          {application.status ===
                            'approved' ? (

                            <span
                              className={`glass-status-badge ${
                                application.certificate_status ===
                                'submitted'
                                  ? 'active'
                                  : 'pending'
                              }`}
                            >

                              {
                                application.certificate_status ===
                                'submitted'
                                  ? 'Submitted'
                                  : 'Not Submitted'
                              }

                            </span>

                          ) : (

                            <span className="dash-exec-email">
                              —
                            </span>

                          )}

                        </td>


                        {/* ACTION */}

                        <td className="right">

                          <button
                            type="button"
                            className="glass-icon-btn"
                            title="Review application"
                            onClick={() =>
                              openApplication(
                                application
                              )
                            }
                            disabled={
                              detailLoading
                            }
                          >

                            <span className="material-symbols-outlined">
                              visibility
                            </span>

                          </button>

                        </td>

                      </tr>

                    )
                  )}

                </tbody>

              </table>

            )}

          </div>

        </div>


        {/* PAGINATION */}

        {orderedApplications.length > 0 && (

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 18
            }}
          >

            <span className="dash-exec-email">

              {(currentPage - 1) *
                PAGE_SIZE +
                1}

              {'–'}

              {Math.min(
                currentPage * PAGE_SIZE,
                orderedApplications.length
              )}

              {' of '}

              {orderedApplications.length}

            </span>


            <div
              style={{
                display: 'flex',
                gap: 6
              }}
            >

              <button
                type="button"
                className="glass-icon-btn"
                disabled={
                  currentPage <= 1
                }
                onClick={() =>
                  setCurrentPage(
                    (page) =>
                      Math.max(
                        1,
                        page - 1
                      )
                  )
                }
              >
                <span className="material-symbols-outlined">
                  chevron_left
                </span>
              </button>


              {Array.from(
                {
                  length: totalPages
                },
                (_, index) =>
                  index + 1
              ).map((page) => (

                <button
                  key={page}
                  type="button"
                  className={`glass-tab ${
                    currentPage === page
                      ? 'active'
                      : ''
                  }`}
                  onClick={() =>
                    setCurrentPage(page)
                  }
                >
                  {page}
                </button>

              ))}


              <button
                type="button"
                className="glass-icon-btn"
                disabled={
                  currentPage >=
                  totalPages
                }
                onClick={() =>
                  setCurrentPage(
                    (page) =>
                      Math.min(
                        totalPages,
                        page + 1
                      )
                  )
                }
              >
                <span className="material-symbols-outlined">
                  chevron_right
                </span>
              </button>

            </div>

          </div>

        )}

      </div>


      {/* =====================================================
          APPLICATION DETAIL MODAL
         ===================================================== */}

      {selectedApplication && (

        <div className="dash-modal-backdrop">

          <div className="dash-modal certificate-application-modal">

            {/* HEADER */}

            <div className="dash-modal-header">

              <div className="glass-modal-title">

                <h3>
                  {
                    TYPE_LABELS[
                      selectedApplication.application_type
                    ]
                  } Application
                </h3>

                <p>
                  {selectedApplication.vendor_name}
                  {' · '}
                  ID #{selectedApplication.id}
                </p>

              </div>


              <button
                type="button"
                className="glass-icon-btn"
                aria-label="Close application details"
                title="Close"
                onClick={() =>
                  setSelectedApplication(null)
                }
              >

                <span className="material-symbols-outlined">
                  close
                </span>

              </button>

            </div>


            {/* SUMMARY */}

            <div className="certificate-modal-content">

              <div className="certificate-summary-grid">

                <div className="certificate-summary-item">
                  <span>Application Type</span>
                  <strong>
                    {
                      TYPE_LABELS[
                        selectedApplication.application_type
                      ]
                    }
                  </strong>
                </div>

                <div className="certificate-summary-item">
                  <span>Application Status</span>
                  <strong>
                    {
                      STATUS_LABELS[
                        selectedApplication.status
                      ] ||
                      selectedApplication.status
                    }
                  </strong>
                </div>

                <div className="certificate-summary-item">
                  <span>Certificate Status</span>
                  <strong>
                    {selectedApplication.status ===
                    'approved'
                      ? selectedApplication.certificate_status ===
                        'submitted'
                        ? 'Submitted'
                        : 'Not Submitted'
                      : '—'}
                  </strong>
                </div>

                <div className="certificate-summary-item">
                  <span>Application ID</span>
                  <strong>
                    #{selectedApplication.id}
                  </strong>
                </div>

              </div>


              {/* APPLICATION DETAILS */}

              <div className="certificate-detail-section">

                <h4>
                  Application Details
                </h4>

                <div className="certificate-detail-grid">

                  {Object.entries(
                    selectedApplication
                  )
                    .filter(
                      ([key]) =>
                        ![
                          'documents',
                          'id',
                          'vendor_id',
                          'application_type',
                          'application_label',
                          'certificate_status',
                          'certificate_submitted_at'
                        ].includes(key)
                    )
                    .map(
                      ([key, value]) => (

                        <div
                          key={key}
                          className="certificate-detail-item"
                        >

                          <span>
                            {key
                              .replaceAll(
                                '_',
                                ' '
                              )
                              .replace(
                                /^\w/,
                                (c) =>
                                  c.toUpperCase()
                              )}
                          </span>

                          <strong>
                            {value === null ||
                            value === ''
                              ? '—'
                              : String(value)}
                          </strong>

                        </div>

                      )
                    )}

                </div>

              </div>


              {/* =================================================
                  CERTIFICATE PROCESSING
                 ================================================= */}

              {selectedApplication.status ===
                'approved' && (

                <div className="certificate-processing-panel">

                  <div className="certificate-processing-header">

                    <div>

                      <h4>
                        Certificate Processing
                      </h4>

                      <p>
                        Track certificate submission
                        after application approval.
                      </p>

                    </div>

                    <span
                      className={`glass-status-badge ${
                        selectedApplication.certificate_status ===
                        'submitted'
                          ? 'active'
                          : 'pending'
                      }`}
                    >
                      {
                        selectedApplication.certificate_status ===
                        'submitted'
                          ? 'Submitted'
                          : 'Not Submitted'
                      }
                    </span>

                  </div>


                  <div className="certificate-processing-grid">

                    <div className="certificate-processing-item">

                      <span>
                        Certificate Status
                      </span>

                      <strong>
                        {
                          selectedApplication.certificate_status ===
                          'submitted'
                            ? 'Submitted'
                            : 'Not Submitted'
                        }
                      </strong>

                    </div>


                    <div className="certificate-processing-item">

                      <span>
                        Deadline
                      </span>

                      <strong>
                        {
                          formatDeadline(
                            selectedApplication
                          )
                        }
                      </strong>

                    </div>


                    <div className="certificate-processing-item">

                      <span>
                        Approved On
                      </span>

                      <strong>
                        {
                          formatDate(
                            selectedApplication.reviewed_at
                          )
                        }
                      </strong>

                    </div>


                    <div className="certificate-processing-item">

                      <span>
                        Certificate Submitted On
                      </span>

                      <strong>
                        {
                          formatDate(
                            selectedApplication.certificate_submitted_at
                          )
                        }
                      </strong>

                    </div>

                  </div>


                  {/* BUTTON */}

                  {selectedApplication.certificate_status !==
                    'submitted' && (

                    <div className="certificate-processing-action">

                      <button
                        type="button"
                        className="dash-modal-btn primary"
                        onClick={
                          handleCertificateSubmitted
                        }
                        disabled={
                          certificateLoading
                        }
                      >

                        <span className="material-symbols-outlined">
                          task_alt
                        </span>

                        {certificateLoading
                          ? 'Updating...'
                          : 'Mark Certificate Submitted'}

                      </button>

                    </div>

                  )}

                </div>

              )}


              {/* DOCUMENTS */}

              <div className="certificate-detail-section">

                <h4>
                  Verification Documents
                </h4>

                {selectedApplication.documents?.length ? (

                  <div className="glass-doc-chips">

                    {selectedApplication.documents.map(
                      (document) => (

                        <a
                          key={document.id}
                          href={document.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className={`glass-doc-chip ${
                            document.review_status ===
                            'approved'
                              ? 'approved'
                              : document.review_status ===
                                'rejected'
                              ? 'rejected'
                              : ''
                          }`}
                        >

                          <span className="material-symbols-outlined">
                            description
                          </span>

                          {
                            document.original_file_name ||
                            document.document_type
                          }

                        </a>

                      )
                    )}

                  </div>

                ) : (

                  <p className="dash-exec-email">
                    No documents uploaded.
                  </p>

                )}

              </div>


              {/* ACTIONS */}

              {selectedApplication.status === 'submitted' ||
 selectedApplication.status === 'under_review' ? (
  <div
    className="dash-modal-actions"
    style={{
      marginTop: 28
    }}
  >
    <button
      type="button"
      className="dash-modal-btn secondary"
      onClick={openReject}
      disabled={actionLoading}
    >
      Reject
    </button>

    <button
      type="button"
      className="dash-modal-btn primary"
      onClick={handleApprove}
      disabled={actionLoading}
    >
      {actionLoading
        ? 'Processing...'
        : 'Approve'}
    </button>
  </div>
) : null}

            </div>

          </div>

        </div>

      )}


      {/* =====================================================
          REJECTION MODAL
         ===================================================== */}

      {rejectTarget && (

        <div className="dash-modal-backdrop">

          <form
            className="dash-modal rejection-modal"
            onSubmit={handleReject}
          >

            <div className="dash-modal-header">

              <div>

                <h3>
                  Reject Application
                </h3>

                <p className="dash-exec-email">
                  {
                    rejectTarget.vendor_name
                  }
                </p>

              </div>


              <button
                type="button"
                className="glass-icon-btn"
                onClick={() =>
                  setRejectTarget(null)
                }
              >

                <span className="material-symbols-outlined">
                  close
                </span>

              </button>

            </div>


            <div className="certificate-modal-content">

              <div className="dash-modal-field">

                <label>
                  Rejection remarks
                </label>

                <textarea
                  className="admin-text-input"
                  rows={5}
                  value={rejectRemarks}
                  onChange={(event) =>
                    setRejectRemarks(
                      event.target.value
                    )
                  }
                  placeholder="Explain why the application was rejected..."
                  required
                />

              </div>


              <div className="dash-modal-actions">

                <button
                  type="button"
                  className="dash-modal-btn secondary"
                  onClick={() =>
                    setRejectTarget(null)
                  }
                >
                  Cancel
                </button>


                <button
                  type="submit"
                  className="dash-modal-btn primary"
                  disabled={
                    actionLoading
                  }
                >
                  {actionLoading
                    ? 'Rejecting...'
                    : 'Reject Application'}
                </button>

              </div>

            </div>

          </form>

        </div>

      )}

    </AdminShell>
  );
}